import { type TestId } from '@repo/test-id';
import { ArrowLeftIcon, ShareNodesIcon, LocationPinIcon } from '@repo/icons';
import { useNavigate } from 'react-router-dom';
import type { OwnedParking } from '@repo/domain';
import { formatAddressLine } from '@repo/domain-service';
import { tr } from '@repo/locale';

import { useParkingShare } from '@features/ownedParkingManage/hooks/useParkingShare';
import ManageParkingDraftContainer from '@features/ownedParkingManage/components/manage/ManageParkingDraftContainer';
import ManageParkingStatusInfo from '@features/ownedParkingManage/components/manage/ManageParkingStatusInfo';
import SignageMissingAlert from '@features/ownedParkingManage/components/signage/SignageMissingAlert';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

import whiteEllipseBackground from '@assets/images/parking-manage/white-ellipse-background.svg';
import parkingSign from '@assets/images/parking-manage/parking-sign-post.png';
import citySkyline from '@assets/images/city-skyline-negative.webp';

interface Props {
  parking: OwnedParking;
  testId: TestId;
}

export default function ManageHeader({ parking, testId }: Props) {
  const navigate = useNavigate();
  const { shareParking } = useParkingShare(parking.slug, testId);

  const handleGoBack = () => {
    navigate(ROUTE_PATHS[RouteName.MY_PARKINGS]);
  };
  const handleShare = () => {
    shareParking();
  };

  return (
    <div className="relative">
      <div
        data-testid={testId}
        className="bg-brand-2 relative z-5 flex h-45 overflow-hidden rounded-b-2xl text-white md:rounded-2xl"
      >
        <img src={citySkyline} className="absolute bottom-0 opacity-5" alt="" />
        <img src={parkingSign} className="absolute top-25 -right-4 w-18" alt="" />
        <img src={whiteEllipseBackground} className="mx-auto mt-auto w-full max-w-140" alt="" />

        <div className="absolute w-full p-4 pt-8">
          <div className="text-heading flex justify-between">
            <button
              className="cursor-pointer"
              onClick={handleGoBack}
              aria-label={tr('owned_parking_manage:manage.go_to_dashboard')}
            >
              <ArrowLeftIcon size={30} />
            </button>
            {parking.status === 'active' && (
              <button className="flex cursor-pointer items-center gap-1.5" onClick={handleShare}>
                <ShareNodesIcon size={20} />
                <div className="text-base font-medium">{tr('owned_parking_manage:dashboard.actions.share.label')}</div>
              </button>
            )}
          </div>
        </div>
        <div className="absolute bottom-0 w-full overflow-hidden p-4 pr-14">
          <div className="text-body truncate pb-1 font-mono text-xs" data-testid={testId.append('slug')}>
            {parking.slug}
          </div>
          <div className="text-heading mb-1 truncate text-xl font-semibold" data-testid={testId.append('name')}>
            {parking.name}
          </div>
          {parking.address && (
            <div className="text-body flex items-center gap-1">
              <LocationPinIcon className="shrink-0" size={16} />
              <div className="truncate text-sm font-normal" data-testid={testId.append('address')}>
                {formatAddressLine(parking.address)}
              </div>
            </div>
          )}
        </div>
      </div>
      {parking.status === 'draft' && (
        <ManageParkingDraftContainer testId={testId.append('activation')} parking={parking} />
      )}
      {parking.status !== 'draft' && parking.status !== 'active' && (
        <ManageParkingStatusInfo status={parking.status} testId={testId.append('status')} />
      )}
      {parking.status === 'active' && !parking.hasSignage && (
        <SignageMissingAlert parkingId={parking.id} testId={testId.append('signageMissing')} />
      )}
    </div>
  );
}
